<script setup lang="ts">
import { FileText, X } from 'lucide-vue-next'
import type { MoveConfirmRequest } from '~/types/web-chat'

const props = defineProps<{ request: MoveConfirmRequest }>()

const emit = defineEmits<{
  'confirm-move': []
  'cancel-move': []
}>()

const { t } = useI18n()

/**
 * The line shown once the card is settled, read from `request.outcome` — what the move actually did.
 * `status === 'confirmed'` is set before the move runs, so on its own it is not success (MAR-957).
 */
const settledLine = computed(() => {
  const { status, outcome } = props.request
  if (status === 'cancelled') return { text: t('webChat.confirmCards.move.cancelled'), failed: false }
  if (!outcome) return { text: t('webChat.confirmCards.move.working'), failed: false }
  if (outcome.ok) return { text: t('webChat.confirmCards.move.done'), failed: false }
  return { text: t('webChat.confirmCards.move.failed'), failed: true }
})
</script>

<template>
  <div class="mt-3 rounded-lg border border-primary/20 bg-primary-lighter p-3">
    <div class="flex items-start gap-2">
      <div class="mt-0.5 h-5 w-5 rounded-full bg-primary-light flex items-center justify-center shrink-0">
        <FileText class="h-3 w-3 text-primary" />
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-xs font-medium text-foreground">{{ t('webChat.confirmCards.move.title') }}</p>
        <i18n-t keypath="webChat.confirmCards.move.body" tag="p" class="mt-0.5 text-xs text-muted-foreground leading-relaxed" scope="global">
          <template #target>
            <span class="font-medium text-foreground">
              {{ request.items.length === 1 ? `"${request.items[0]?.name}"` : t('webChat.confirmCards.itemCount', { n: request.items.length }) }}
            </span>
          </template>
          <template #destination>
            <span class="font-medium text-foreground">{{ request.destination }}</span>
          </template>
        </i18n-t>
        <div
          v-if="request.items.length > 1"
          class="mt-1 text-[11px] text-muted-foreground/80 leading-relaxed"
        >
          {{ request.items.slice(0, 3).map(item => item.name).join(', ') }}{{ request.items.length > 3 ? ` ${t('webChat.confirmCards.andMore', { n: request.items.length - 3 })}` : '' }}
        </div>
        <div
          v-if="request.status === 'pending'"
          class="mt-2 flex flex-wrap gap-1.5"
        >
          <Button
            type="button"
            size="sm"
            class="gap-1.5"
            @click="emit('confirm-move')"
          >
            <FileText class="h-3 w-3" />
            {{ t('webChat.confirmCards.move.action') }}
          </Button>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent-lighter hover:text-foreground transition-colors"
            @click="emit('cancel-move')"
          >
            <X class="h-3 w-3" />
            {{ t('webChat.confirmCards.cancel') }}
          </button>
        </div>
        <p
          v-else
          class="mt-2 text-[11px]"
          :class="settledLine.failed ? 'text-destructive' : 'text-muted-foreground'"
        >
          {{ settledLine.text }}
        </p>
      </div>
    </div>
  </div>
</template>
